import React from 'react';
import classnames from 'classnames';
import {
  Typography
} from '@one-thd/sui-atomic-components';
import { Image } from './Image';
import { List } from './List';
import { TabContent } from './TabContent';
import { Text } from './Text';
import { Tip } from './Tip';


  const buildSectionClassnames = ({
    subIndex,
    flexGroup = false,
    hasBorder = false,
    shiftLeft = false,
    marginOverride = null,
    marginTopOverride = null,
  }) => {
    return classnames({
      'sui-mb-4': subIndex === 0,
      'sui-mb-2': subIndex === 1,
      'sui-mb-1': subIndex >= 2,
      'sui-ml-6': shiftLeft,
      'sui-flex sui-justify-center sui-gap-4': flexGroup,
      'sui-border-solid sui-border-1 sui-border-primary sui-rounded-base': hasBorder,
      '!sui-mb-0': marginOverride === 0,
      '!sui-mb-1': marginOverride === 1,
      '!sui-mb-2': marginOverride === 2,
      '!sui-mb-3': marginOverride === 3,
      '!sui-mb-4': marginOverride === 4,
      '!sui-mb-5': marginOverride === 5,
      '!sui-mb-6': marginOverride === 6,
      '!sui-mb-8': marginOverride === 8,
      '!sui-mt-2': marginTopOverride === 2,
    });
  };

export const BuildDrawer = (drawerSections, subIndex = 0, selectedTab, setSelectedTab, hasCustomHeader = false) => {
    return drawerSections.map((section) => {
      if (Array.isArray(section)) return BuildDrawer(section, subIndex + 1);
      const [[type, content], [_, modifiers = {}] = []] = Object.entries(section);
      const {
        flexGroup,
        hasBorder,
        shiftLeft,
        marginOverride,
        marginTopOverride,
        sideBySide,
        stacked,
        centered,
        separator
      } = modifiers;

      const sectionClassnames = buildSectionClassnames({
        subIndex,
        flexGroup,
        hasBorder,
        shiftLeft,
        marginOverride,
        marginTopOverride,
        sideBySide,
        stacked,
        centered,
        separator
      });

      switch (type) {
        case 'text': {
          const {
            id, string: textString, isTitle, isTiny, boldString, linkString, linkUrl
          } = content;
          if (hasCustomHeader && isTitle) return null;
          return (
            <div key={id} className={sectionClassnames}>
              <Text
                text={textString}
                isTitle={isTitle}
                isTiny={isTiny}
                boldText={boldString}
                linkText={linkString}
                linkUrl={linkUrl}
              />
            </div>
          );
        }
        case 'list': {
          const { id, items } = content;
          return (
            <div key={id} className={sectionClassnames}>
              <List list={items} />
            </div>
          );
        }
        case 'image': {
          const {
            id,
            src,
            alt,
            size,
            subHeader,
            subSubHeader
          } = content;
          return (
            <div key={id} className={sectionClassnames}>
              <Image
                src={src}
                alt={alt}
                size={size}
                subHeader={subHeader}
                subSubHeader={subSubHeader}
                sideBySide={sideBySide}
                stacked={stacked}
                centered={centered}
                separator={separator}
              />
            </div>
          );
        }
        case 'tip': {
          const { id, string: tipString, sections: tipSections = [] } = content;
          return (
            <div key={id} className={sectionClassnames}>
              <Tip text={tipString}>
                <>{tipSections.length ? BuildDrawer(tipSections, subIndex + 1) : null}</>
              </Tip>
            </div>
          );
        }
        case 'sections': {
          const { id, items } = content;
          return (
            <div key={id} className={sectionClassnames}>
              {items.map((item, index) => (
                <React.Fragment key={item.id || index}>
                  {BuildDrawer([item], subIndex + 1, selectedTab, setSelectedTab)}
                </React.Fragment>
              ))}
            </div>
          );
        }
        case 'imageSeparator': {
          const { id, items, separator: separatorString } = content;
          const wrapperClassnames = 'sui-flex sui-items-stretch sui-justify-center sui-gap-8 sui-mb-6 sui-max-w-[75%]';
          const imageClassnames = 'sui-flex sui-flex-col sui-gap-4 sui-items-center sui-min-w-24 sui-justify-end';
          const isOrSeparator = separatorString === 'or';
          const separatorClassnames = `sui-m-auto sui-pb-6 ${isOrSeparator ? 'sui-text-3xl' : 'sui-text-6xl'}`;
          return (
            <div key={id} className="sui-flex sui-justify-center">
              <div className={wrapperClassnames}>
                {items.map((item, index) => {
                  const showSeparator = !!separatorString && index < items.length - 1;
                  const { image } = item;
                  const {
                    id: imageId, src, alt, height, width, subHeader
                  } = image;

                  return (
                    <React.Fragment key={imageId}>
                      <div className={imageClassnames}>
                        <img
                          key={`image - ${src}`}
                          src={src}
                          alt={`Image: ${alt}`}
                          height={height}
                          width={width}
                        />
                        {subHeader && (
                          <div key={`${subHeader} - ${src}`} className="sui-text-nowrap">
                            <Typography
                              variant="body-xs"
                              component="span"
                            >
                              {subHeader}
                            </Typography>
                          </div>
                        )}
                      </div>
                      {showSeparator && (
                        <div key={`separator - ${src}`} className="sui-flex sui-items-center">
                          <span className={separatorClassnames}>
                            {separatorString === 'plus' ? '+' : 'or'}
                          </span>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        }
        case 'tabs': {
          const { id, items } = content;
          return (
            <div key={id} className={sectionClassnames}>
              <TabContent
                id={id}
                items={items}
                buildDrawer={BuildDrawer}
                selectedTab={selectedTab}
                onTabChange={(tabId, newValue) => { setSelectedTab(newValue); }}
              />
            </div>
          );
        }
        default: {
          return null;
        }
      }
    });
  };